import { http, getAccessToken } from '@/utils/httpClient'
import { resolveInternalApiUrl } from '@/utils/apiHeaders'

// Dotted event types as emitted by the backend (notification.schema.ts): the
// prefix is the entity ("bug"), the suffix is the action ("assigned").
export type NotificationType =
  | 'bug.assigned'
  | 'bug.status_changed'
  | 'bug.created'
  | 'accessibility.assigned'
  | 'accessibility.status_changed'
  | 'feature.status_changed'
  | 'feature.dev_assigned'
  | 'feature.qa_assigned'
  | 'feature.created'
  | 'testcase.assigned'
  | 'testrun.completed'
  | 'testrun.status_changed'
  | 'system'

export interface NotificationView {
  _id: string
  type: NotificationType
  actor_id: string | null
  actor_name: string | null
  entity_ref: string | null // entity Mongo _id (stringified)
  entity_id: string | null
  entity_title: string | null
  project_id: string | null
  metadata: Record<string, any>
  read_at: string | null
  createdAt: string
}

export interface NotificationListResult {
  data: NotificationView[]
  total: number
  page: number
  limit: number
  pages: number
  unread_count: number
}

export interface ListNotificationsParams {
  read?: boolean
  page?: number
  limit?: number
}

class NotificationService {
  private readonly baseUrl = '/notifications'
  private db: any = null

  // For compatibility with the BaseService pattern (entity services are
  // wired with the db adapter at startup, even when they call REST directly).
  setDatabaseService(databaseService: any) {
    this.db = databaseService
  }

  list(params: ListNotificationsParams = {}): Promise<NotificationListResult> {
    return http.get(this.baseUrl, { params })
  }

  // Just the unread badge count — one indexed count query, no list/populate.
  unreadCount(): Promise<{ unread_count: number }> {
    return http.get(`${this.baseUrl}/unread-count`)
  }

  markRead(id: string): Promise<NotificationView> {
    return http.patch(`${this.baseUrl}/${id}/read`, {})
  }

  markAllRead(): Promise<{ updated: number }> {
    return http.patch(`${this.baseUrl}/read-all`, {})
  }

  // Absolute URL for the realtime SSE stream, with the JWT on the query string.
  // The browser's EventSource API can't set an Authorization header, so the
  // backend's SSE route reads the token from `access_token` (see JwtSseAuthGuard).
  // Returns null when there's no token (logged out) — callers should not connect.
  streamUrl(): string | null {
    const token = getAccessToken()
    if (!token) return null
    const base = resolveInternalApiUrl(`${this.baseUrl}/stream`)
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}access_token=${encodeURIComponent(token)}`
  }
}

export const notificationService = new NotificationService()
